import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { CampaignProvider } from '../../contexts/campaign';
import { MATERIAL_ICON_DOWNLOAD, MATERIAL_ICON_VIDEO } from '../../shared/common/Button/Icon';
import { FilterBar } from '../../shared/common/FilterBar';
import Pagination, { getPaginationStateObject } from '../../shared/common/Pagination';
import ProductList from '../../shared/common/ProductList';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../shared/components/NoElementsFound';
import VideoPlayerPopup from '../../shared/components/VideoPlayerPopup';
import { BannerSlider } from '../../shared/slider/BannerSlider';
import ContentBaseLayout from '../../yourborrowbox/libx/shared/ContentBaseLayout';
import { CampaignListItem } from './CampaignListItem';
import { CampaignProductTile } from './CampaignProductTile';
import { Button, ButtonSize } from '../../shared/common/Button';
import { useHistory, useParams } from 'react-router';
import { useBannersQuery } from '../useBannersQuery';
import { invalidateCampaignProducts, useCampaignProducts } from './api/useCampaignProducts';
import queryString from 'query-string';
import { useQueryParams } from '../../shared/hooks/useQueryParams';
import { ProductSorting } from '../../../models/Product';
import { modifyURLState, modifyURLStates } from '../../shared/utils/utils-routing';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { DisplayTypeState } from '../../store/displayType/state';
import { displayCampaigns } from '../../store/header/actions';
import { useOrderLists } from '../../shared/common/useOrderLists';
import { getLoanFormat } from '../../shared/utils/getLoanFormat';
import { ageCategoryFromString } from '../../../models/AgeCategory';
import { ResponsiveFilterBar } from '../../shared/filter/ResponsiveFilterBar';
import { fetchCampaignProductsAggregationsQuickSearch } from '../../api/campaign-products/fetchCampaignProductsAggregationsQuickSearch';
import { AppState } from '../../store/appState';
import { QueryKeyBuilder } from '../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { fetchCampaignProductsAggregations } from '../../api/campaign-products/fetchCampaignProductsAggregations';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { downloadCampaignGuide } from '../../api/marketing/your-campaigns/downloadCampaignGuide';
import { useTranslation } from 'react-i18next';
import { useAddToList } from '../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';

const StTrainingButtonContainer = styled.div`
    align-self: center;
    margin-bottom: 32px;

    * ~ * {
        margin-left: 8px;
    }
`;

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';
const DEFAULT_PAGE_SIZE = 54;
// This is a temporary solution that we came up with Alex
// The product tile component styling is not styled correctly and not behaving as intened.
//We will find a better solution for the whole ProductTile component.
// https://borrowbox.atlassian.net/browse/PORTAL-4568
const StPagination = styled(Pagination)`
    margin-top: 200px;
`;

export const CampaignProductsPage = () => {
    const { t } = useTranslation();
    const [showIntroVideo, setShowIntroVideo] = useState(false);
    const [urlParamHandled, setUrlParamHandled] = useState(false);

    const dispatch = useDispatch();
    const authentication = useSelector((state: AppState) => state.authentication);
    const { location, replace } = useHistory();

    const { loanformat: loanFormatPathParam } = useParams<{ loanformat: string }>();
    const loanFormat = getLoanFormat(loanFormatPathParam);

    const { orderlists } = useOrderLists();
    const { data: banners, isLoading: isBannersLoading } = useBannersQuery('CAMPAIGN_PRODUCTS');

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(location.search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        filter: { value: filterQueryParam },
        sortBy: { value: sortByParam },
        display: { value: display },
        ageCategory: { value: ageCategoryQueryParam }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        pageSize: 'integer',
        filter: 'string',
        [SORT_BY_QUERY_PARAM]: 'string',
        display: 'string',
        ageCategory: 'string'
    });

    const filter = deserializeFilter(filterQueryParam);
    const page = pageParam - 1 ?? 0;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_OLD_TO_NEW;
    const ageCategory = ageCategoryFromString(ageCategoryQueryParam);

    useLayoutEffect(() => {
        // Aggregation has a dependency which is View Hierarchy. We need to set the view hierarchy state to Campaigns here to be able to fetch the correct endpoint.
        dispatch(displayCampaigns());
    }, [dispatch]);

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => defaultVisibleFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => {
            return [
                'campaignProductsPage',
                loanFormat.requestKey!,
                'aggregations',
                ageCategory,
                filters,
                expandedFilterGroups
            ];
        },
        [ageCategory, loanFormat.requestKey]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchCampaignProductsAggregations({ ...options, loanFormat: loanFormat.requestKey!, ageCategory }),
        [ageCategory, loanFormat.requestKey]
    );

    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchCampaignProductsAggregationsQuickSearch({
                ...options,
                loanFormat: loanFormat.requestKey!,
                ageCategory
            }),
        [ageCategory, loanFormat.requestKey]
    );
    //endregion

    const {
        list: products,
        totalHits,
        isLoading: isProductsLoading
    } = useCampaignProducts({
        filterMap: filter,
        loanFormat: loanFormat.requestKey!,
        ageCategory,
        pagination: {
            page,
            pageSize,
            sortBy
        }
    });

    const loading = (isProductsLoading || isBannersLoading) ?? false;

    useEffect(() => {
        if (!loading && !showIntroVideo) {
            const searchQuery = location?.search ?? '';
            if (searchQuery.includes('intro') && !urlParamHandled) {
                setShowIntroVideo(true);
                setUrlParamHandled(true);
            }
        }
    }, [loading, location.search, showIntroVideo, urlParamHandled]);

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(location.search, 'displayFilter', !displayFilter, false);
        replace({ pathname: location.pathname, search: nextSearch });
    }

    function onDisplayChange(nextDisplayType) {
        const nextSearch = modifyURLState(location.search, 'display', nextDisplayType, false);
        replace({ pathname: location.pathname, search: nextSearch });
    }

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart(() => {
        invalidateCampaignProducts().then();
    });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps } = useListContentChecks<SingleAddOptions>({ onAction });

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(location.search, [pageQueryState, sortByQueryState]);

        replace({ pathname: location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        replace({ pathname: location.pathname, search: nextSearch });
    };

    return (
        <>
            {isEmpty(banners) ? null : (
                <BannerSlider itemWidth={2000} itemHeight={300} showButtons={false} showDots={true} showShadows={false}>
                    {banners?.map((bannerItem, i) => {
                        if (bannerItem.targetUrl) {
                            return (
                                <Link to={bannerItem.targetUrl} key={i}>
                                    <img src={bannerItem.imageUrl} />
                                </Link>
                            );
                        } else {
                            return <img src={bannerItem.imageUrl} key={i} />;
                        }
                    })}
                </BannerSlider>
            )}
            <ContentBaseLayout
                topComponent={
                    <>
                        <StTrainingButtonContainer>
                            <Button
                                onClick={() => setShowIntroVideo(true)}
                                text={t('shop.campaigns.howToRunACampaign')}
                                icon={MATERIAL_ICON_VIDEO}
                                size={ButtonSize.LARGE}
                            />
                            <Button
                                onClick={() => downloadCampaignGuide()}
                                text={t('shop.campaigns.campaignGuide')}
                                icon={MATERIAL_ICON_DOWNLOAD}
                                size={ButtonSize.LARGE}
                            />
                        </StTrainingButtonContainer>
                        <FilterBar
                            currentPage={page}
                            onPageChange={setPage}
                            hideGroupBy={true}
                            hideSelectButtons={true}
                            excludeSorting={['stock']}
                            totalProducts={totalHits}
                            onSortChange={setSortBy}
                            dispatch={dispatch}
                            sortBy={sortBy}
                            displayType={display as DisplayTypeState}
                            onFilterDisplayToggle={onFilterDisplayToggle}
                            onDisplayChange={onDisplayChange}
                            displayFilter={displayFilter}
                        />
                    </>
                }
                sidebarComponent={
                    displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                        />
                    ) : null
                }
                contentComponent={
                    products && (products.length ?? 0) > 0 && !loading ? (
                        <CampaignProvider value={{ preventCampaignBadge: true }}>
                            <div className="ca-main">
                                <div className="ca-main-inner">
                                    <ProductList
                                        displayType={display}
                                        currentPage={page}
                                        orderlists={orderlists}
                                        products={products}
                                        productSelection={null}
                                        listItemComponent={CampaignListItem}
                                        tileItemComponent={CampaignProductTile}
                                        onAddPrice={changePrice}
                                    />
                                    <StPagination
                                        totalItems={totalHits}
                                        onPageChange={setPage}
                                        currentPage={page}
                                        pageSize={pageSize}
                                    />
                                </div>
                            </div>
                        </CampaignProvider>
                    ) : (
                        <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                    )
                }
                documentTitle={t('shop.campaigns.campaigns')}
                modalComponent={
                    <VideoPlayerPopup
                        showVideo={showIntroVideo}
                        hideVideo={() => setShowIntroVideo(false)}
                        vimeoId={354627777}
                    />
                }
                loading={loading}
            />
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
        </>
    );
};
